import { Card, Table, Button, Modal, Form, Input, Space, Typography, Tooltip, Alert } from "antd";
import React, { useEffect, useState } from "react";
import useRequest from "../hooks/useRequest";
import { ShowToast, Severty } from "../helper/toast";
import apiPath from "../constants/apiPath";
import { EditOutlined, EyeOutlined } from "@ant-design/icons";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
const { Text } = Typography;

/** Must match backend defaults — merge tags in <a href> are stripped by ReactQuill, so those templates use raw HTML. */
const RECOMMENDED_FORGOT_PASSWORD_HTML =
  '<p>Hi {{name}},</p><p>We received a request to reset your password.</p><p><a href="{{resetLink}}" style="display:inline-block;margin:8px 0;padding:10px 20px;background:#e67e22;color:#ffffff !important;text-decoration:none;border-radius:6px;font-weight:600;">Reset Password</a></p><p style="margin-top:16px;">This link is valid for <strong>10 minutes</strong>.</p><p>If you did not request this, you can ignore this email.</p>';

const RECOMMENDED_WELCOME_HTML =
  "<p>Hi {{name}},</p><p>Welcome to Project.Kitchen. Please activate your account by clicking the link below:</p><p><a href=\"{{activationLink}}\">Activate Account</a></p><p>Thanks,<br/>Project.Kitchen Team</p>";

const TEMPLATES_USE_RAW_HTML = new Set(["forgot_password", "welcome_activation"]);

const getEmailPreviewSampleData = () => {
  const origin =
    typeof window !== "undefined" && window.location?.origin
      ? window.location.origin
      : "http://localhost:3000";
  return {
    name: "Admin",
    resetLink: `${origin}/reset-password?email=admin%40example.com&otp=123456`,
    activationLink: `${origin}/activate?token=demo-sample-token`,
    otp: "123456",
    message: "Sample notification message.",
  };
};

/** Replace {{placeholders}} so preview matches a real sent email (same rules as backend). */
const applyEmailPreviewPlaceholders = (templateString) => {
  if (templateString == null) return "";
  let out = String(templateString);
  const data = getEmailPreviewSampleData();
  Object.entries(data).forEach(([key, value]) => {
    out = out.replace(new RegExp(`{{\\s*${key}\\s*}}`, "g"), value);
  });
  return out;
};

const withTooltip = (value) => {
  const text = value == null || value === "" ? "—" : String(value);
  return (
    <Tooltip title={text}>
      <span>{text}</span>
    </Tooltip>
  );
};

function EmailTemplate() {
  const { request } = useRequest();
  const [form] = Form.useForm();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [editorContent, setEditorContent] = useState("");
  const [saving, setSaving] = useState(false);

  const columns = [
    {
      title: "Template Name",
      dataIndex: "name",
      key: "name",
      render: (v) => withTooltip(v),
    },
    {
      title: "Template Key",
      dataIndex: "key",
      key: "key",
      render: (v) => <Text code>{v}</Text>,
    },
    {
      title: "Subject",
      dataIndex: "subject",
      key: "subject",
      render: (v) => withTooltip(v),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => {
        return (
          <Space>
            <Button
               className="table-view-button"
               type="primary"
              icon={<EyeOutlined />}
              onClick={() => {
                setSelected(record);
                setPreviewOpen(true);
              }}
            />
            <Button
              type="primary"
                className="table-edit-button"
              icon={<EditOutlined />}
              onClick={() => {
                setSelected(record);
                setEditorContent(record.bodyHtml || "");
                form.setFieldsValue({
                  subject: record.subject,
                  bodyHtml: record.bodyHtml || "",
                });
                setEditOpen(true);
              }}
            />
          </Space>
        );
      },
    },
  ];

  const fetchData = () => {
    setLoading(true);
    request({
      url: apiPath.authAdminEmailTemplates,
      method: "GET",
      onSuccess: (data) => {
        setLoading(false);
        setList(data?.templates || data?.data?.templates || []);
      },
      onError: (error) => {
        setLoading(false);
        const msg = error?.response?.data?.message || error?.message || "Failed to fetch templates";
        ShowToast(msg, Severty.ERROR);
      },
    });
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onUpdate = (values) => {
    if (!selected?.key) return;
    if (!editorContent || !String(editorContent).replace(/<(.|\n)*?>/g, "").trim()) {
      ShowToast("Please enter body content", Severty.ERROR);
      return;
    }
    setSaving(true);
    request({
      url: `${apiPath.authAdminEmailTemplates}/${selected.key}`,
      method: "PUT",
      data: {
        subject: values.subject,
        bodyHtml: editorContent,
      },
      onSuccess: (data) => {
        setSaving(false);
        ShowToast(data?.message || "Template updated", Severty.SUCCESS);
        setEditOpen(false);
        fetchData();
      },
      onError: (error) => {
        setSaving(false);
        const msg = error?.response?.data?.message || error?.message || "Failed to update template";
        ShowToast(msg, Severty.ERROR);
      },
    });
  };

  return (
    <>
      <div className="tabled">
        <Card
          bordered={false}
          className="criclebox tablespace mb-24"
          title="Email Templates Manager"
        >
          <div style={{ marginBottom: 12, color: "#afafaf" }}>
            Admin can only edit existing template subject/body. Add/Delete is disabled by design.
          </div>
          <div className="table-responsive">
            <Table
              loading={loading}
              columns={columns}
              dataSource={list}
              pagination={false}
              rowKey={(record) => record._id || record.key}
              className="ant-border-space"
            />
          </div>
        </Card>
      </div>
      <Modal
        open={editOpen}
        title="Edit Email Template"
        okText="Save Changes"
        onCancel={() => setEditOpen(false)}
        okButtonProps={{ form: "edit-email-template-form", htmlType: "submit", loading: saving }}
        width={900}
      >
        <Form id="edit-email-template-form" form={form} onFinish={onUpdate} layout="vertical">
          <Form.Item label="Template Name">
            <Input value={selected?.name || ""} disabled />
          </Form.Item>
          <Form.Item label="Template Key">
            <Input value={selected?.key || ""} disabled />
          </Form.Item>
          <Form.Item
            label="Subject"
            name="subject"
            rules={[{ required: true, message: "Please enter subject" }]}
          >
            <Input />
          </Form.Item>
          {TEMPLATES_USE_RAW_HTML.has(selected?.key) && (
            <Alert
              showIcon
              type="info"
              style={{ marginBottom: 12 }}
              message="Use raw HTML for this template"
              description={
                selected?.key === "forgot_password" ? (
                  <>
                    The rich text editor removes or breaks <code>{`href="{{resetLink}}"`}</code> because it is not a
                    normal URL. Keep both a linked &quot;Reset Password&quot; line and a plain line with{" "}
                    <code>{"{{resetLink}}"}</code> so every inbox can show the real link.
                  </>
                ) : (
                  <>
                    Keep <code>{"{{activationLink}}"}</code> inside the activation button{" "}
                    <code>href</code> exactly — the visual editor cannot save that safely.
                  </>
                )
              }
            />
          )}
          <Form.Item label="Body">
            {TEMPLATES_USE_RAW_HTML.has(selected?.key) ? (
              <Space direction="vertical" style={{ width: "100%" }} size="middle">
                <Button
                  type="default"
                  size="small"
                  onClick={() => {
                    if (selected?.key === "forgot_password") {
                      setEditorContent(RECOMMENDED_FORGOT_PASSWORD_HTML);
                    } else if (selected?.key === "welcome_activation") {
                      setEditorContent(RECOMMENDED_WELCOME_HTML);
                    }
                  }}
                >
                  Restore recommended HTML
                </Button>
                <Input.TextArea
                  value={editorContent}
                  onChange={(e) => setEditorContent(e.target.value)}
                  rows={14}
                  style={{ fontFamily: "monospace", fontSize: 12 }}
                  spellCheck={false}
                />
              </Space>
            ) : (
              <div className="email-template-editor">
                <ReactQuill value={editorContent} onChange={setEditorContent} />
              </div>
            )}
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={previewOpen}
        title={`Preview - ${selected?.name || "Template"}`}
        footer={null}
        onCancel={() => setPreviewOpen(false)}
        width={900}
      >
        <Typography.Paragraph type="secondary" style={{ marginBottom: 12, fontSize: 13 }}>
          Sample preview: <code>{"{{name}}"}</code>, <code>{"{{resetLink}}"}</code>, and other merge tags are
          filled with demo values. Recipients get real data when the email is sent.
        </Typography.Paragraph>
        <div className="email-template-preview" style={{ marginBottom: 12 }}>
          <b>Subject:</b> {applyEmailPreviewPlaceholders(selected?.subject)}
        </div>
        <style>{`
          .email-template-preview-body {
            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
            font-size: 15px;
            line-height: 1.55;
            color: #1f1f1f;
            background: #fafafa;
          }
          .email-template-preview-body p {
            margin: 0 0 14px;
          }
          .email-template-preview-body p:last-child {
            margin-bottom: 0;
          }
          .email-template-preview-body a {
            color: #1677ff;
            word-break: break-all;
          }
        `}</style>
        <div
          className="email-template-preview email-template-preview-body"
          style={{
            border: "1px solid #e8e8e8",
            borderRadius: 8,
            padding: 20,
            maxHeight: 420,
            overflow: "auto",
          }}
          dangerouslySetInnerHTML={{
            __html: applyEmailPreviewPlaceholders(selected?.bodyHtml || "<p>No body</p>"),
          }}
        />
      </Modal>
    </>
  );
}

export default EmailTemplate;
